/**
 * Hook for subscribing to user presence updates.
 */
import { type PresenceUser, usePresenceContext } from '@datapad/data-model';
import React from 'react';

/**
 * Subscribes to presence events and returns the list of connected remote users.
 *
 * @returns The current list of connected remote users.
 *
 * @remarks
 * Listens to both `remoteUpdated` (state changes from other clients) and
 * `attendeeDisconnected` (clients leaving the session) to keep the list in sync.
 */
export function usePresence(): readonly PresenceUser[] {
	const manager = usePresenceContext();
	const [connectedUsers, setConnectedUsers] = React.useState<readonly PresenceUser[]>(() =>
		manager.getConnectedUsers(),
	);

	React.useEffect(() => {
		function refresh(): void {
			setConnectedUsers(manager.getConnectedUsers());
		}

		const offRemoteUpdated = manager.events.on('remoteUpdated', refresh);
		const offAttendeeDisconnected = manager.attendees.events.on(
			'attendeeDisconnected',
			refresh,
		);

		return () => {
			offRemoteUpdated();
			offAttendeeDisconnected();
		};
	}, [manager]);

	return connectedUsers;
}
